<template>
  <td class="select-entity-cell" @click="focusCell()">
    <div class="d-flex align-center ga-2">
      <v-tooltip v-if="dataField.reference && !relatedEntity" location="bottom" open-delay="100">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" size="small" icon="mdi-alert" color="#ED1C0C" />
        </template>
        <span>Reference is missing related entity</span>
      </v-tooltip>
      <div
        ref="cell"
        class="select-entity-container d-flex align-center ga-2"
        :id="id"
        :tabindex="id"
        :class="{ 'show-select-on-hover': !relatedEntity }"
        @keydown="onKeydown"
      >
        <select-entity
          :entityId="entityId"
          :name="dataField?.name"
          @onSelectEntity="onSelectRelatedEntity"
        >
          <v-chip
            size="small"
            density="compact"
            @click="() => {}"
          >
            {{ relatedEntity?.name || 'Select entity' }}
            <template v-if="!!relatedEntity && !store.state.isWorkflowDisabled" #close>
              <v-icon
                icon="mdi-close-circle"
                color="#888"
                :size="16"
                @click.stop="onSelectRelatedEntity(null)"
                class="remove-entity-icon"
              />
            </template>
          </v-chip>
        </select-entity>
        <toggle
          v-if='isEntity && entityId'
          mandatory
          :value="dataField.cardinality"
          :options="options"
          :disabled="store.state.isWorkflowDisabled"
          @update:modelValue="onSelectCardinality"
        />
      </div>
    </div>
  </td>
</template>

<script setup>
import { computed, ref, useTemplateRef } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { EventBus } from '@/event-bus/event-bus'
import useEntityActions from '@/composables/useEntityActions'

const { id, eventId, entityId, dataField, dataFieldIndex, dataModelIndex, isEntity } = defineProps({
  id: Number,
  eventId: String,
  entityId: String,
  dataField: Object,
  dataFieldIndex: Number,
  dataModelIndex: Number,
  isEntity: Boolean
})
const emit = defineEmits(['leave-cell'])

const options = [
  { value: 'one-to-one', label: '1 : 1' },
  { value: 'one-to-many', label: '1 : N' }
]

const store = useStore()
const cell = useTemplateRef('cell')
const workflow = computed(() => store.state.workflow.workflow)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const { selectRelatedEntity, selectCardinality } = useEntityActions()

const loadingCardinality = ref(false)

const relatedEntity = computed(() => {
  return (workflow.value.dataModels ?? []).find((entity) => entity.id === entityId)
})

const focusCell = () => {
  cell.value.focus()
}

const onKeydown = (event) => {
  event.stopPropagation()

  switch (event.keyCode) {
    case 13: // enter
    case 37: // left
    case 38: // up
    case 39: // right
    case 40: // down
      emit('leave-cell', event)
  }
}

const onSelectRelatedEntity = async (entity) => {
  if (isEntity) {
    await selectRelatedEntity(entity, dataModelIndex, dataFieldIndex)
  } else {
    const updatedSvgGraph = safeStructuredClone(svgGraph.value)
    updatedSvgGraph[eventId].dataModels[dataModelIndex].dataFields[dataFieldIndex].relatedEntityId = entity?.id || null

    await workflowApi.updateWorkflow({
      svgGraph: updatedSvgGraph,
      workflow: workflow.value,
      updateNote: {
        action: 'updated related entity',
        target: ''
      }
    }, true)
  }

  EventBus.$emit('refresh-entity-relationship-diagram')
}

const onSelectCardinality = async (cardinality) => {
  if (loadingCardinality.value) {
    return
  }

  loadingCardinality.value = true

  if (isEntity) {
    await selectCardinality(cardinality, dataModelIndex, entityId)
  } else {
    const updatedSvgGraph = safeStructuredClone(svgGraph.value)

    updatedSvgGraph[eventId].dataModels[dataModelIndex].dataFields.forEach((dataField, index) => {
      if (dataField.relatedEntityId === entityId) {
        updatedSvgGraph[eventId].dataModels[dataModelIndex].dataFields[index].cardinality = cardinality || null
      }
    })

    await workflowApi.updateWorkflow({
      svgGraph: updatedSvgGraph,
      workflow: workflow.value,
      updateNote: {
        action: 'updated data field cardinality',
        target: ''
      }
    }, true)
  }
  loadingCardinality.value = false
}
</script>

<style scoped>
.select-entity-cell {
  border-right: 1px solid #E5E3E1;

  .select-entity-container {
    width: fit-content;
  }

  .show-select-on-hover {
    opacity: 0;
    transition: opacity 0.15s;
  }

  &:hover {
    .show-select-on-hover {
      opacity: 1;
    }
  }
}
</style>
